import React, { useEffect, useState, useCallback } from 'react';
import {
  CheckCircle, ChevronRight, ChevronLeft, Lock, Shield,
  Star, X, Download, Eye, Plus, AlertTriangle, Layers,
  RefreshCw, FileText, List
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────
interface Stage {
  stage_id: number;
  stage_code: string;
  stage_name: string;
  sequence_order: number;
  material_category_count: number;
  material_count: number;
}
interface MaterialCategory {
  category: string;
  display_name: string;
  mat_name: string;
  is_structural: boolean;
  qty_per_sft: number | null;
  wastage_pct: number;
  activity_count: number;
  has_options: boolean;
  options: BrandOption[];
}
interface BrandOption {
  variant_id: number;
  mat_id: number;
  brand_name: string;
  mat_name: string;
  quality_tier: string;
  rate_per_unit: number;
  unit: string;
}
interface Selection {
  variant_id: number;
  mat_id: number;
  brand_name: string;
  mat_name: string;
  quality_tier: string;
  rate_per_unit: number;
  unit: string;
  stage_first_selected: string; // stage_name where first selected
  stage_id_first: number;
}

const TIER_COLORS: Record<string,string> = {
  ECONOMY: 'border-gray-300 bg-gray-50 text-gray-700',
  STANDARD:'border-blue-300 bg-blue-50 text-blue-700',
  PREMIUM: 'border-amber-300 bg-amber-50 text-amber-700',
  LUXURY:  'border-purple-300 bg-purple-50 text-purple-700',
};
const TIER_BADGE: Record<string,string> = {
  ECONOMY:'bg-gray-100 text-gray-600', STANDARD:'bg-blue-100 text-blue-700',
  PREMIUM:'bg-amber-100 text-amber-700', LUXURY:'bg-purple-100 text-purple-700',
};

const inp = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const fmtCr = (n:any) => {
  if (!n) return '—'; const v=Number(n);
  if (v>=10000000) return `₹${(v/10000000).toFixed(2)} Cr`;
  return `₹${(v/100000).toFixed(2)} L`;
};
const fmtDate = (d?:string) => d ? new Date(d).toLocaleDateString('en-IN') : '—';

// ── Brand Picker Card ─────────────────────────────────────────
const BrandPicker = ({
  material, existing, onSelect
}: {
  material: MaterialCategory;
  existing: Selection | null;
  onSelect: (sel: Selection, stageId: number, stageName: string) => void;
}) => {
  const [expanded, setExpanded] = useState(!existing);
  const tiers = ['ECONOMY','STANDARD','PREMIUM','LUXURY'];

  return (
    <div className={`rounded-xl border-2 overflow-hidden transition-all
      ${material.is_structural ? 'border-blue-200' : 'border-gray-200'}`}>

      {/* Header */}
      <div className={`flex items-center gap-3 px-4 py-3 cursor-pointer
        ${material.is_structural ? 'bg-blue-50' : 'bg-white'}`}
        onClick={()=>setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-800">{material.display_name}</span>
            {material.is_structural && (
              <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                <Shield size={9}/> Structural</span>
            )}
            <span className="text-xs text-gray-400">{material.activity_count} activities</span>
          </div>
          {existing ? (
            <div className="flex items-center gap-2 mt-0.5">
              <CheckCircle size={12} className="text-green-500"/>
              <span className="text-xs text-green-700 font-medium">
                {existing.brand_name}
                <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${TIER_BADGE[existing.quality_tier]}`}>
                  {existing.quality_tier}</span>
                · ₹{Number(existing.rate_per_unit).toLocaleString()}/{existing.unit}
              </span>
              {existing.stage_first_selected && (
                <span className="text-xs text-gray-400">
                  (set in: {existing.stage_first_selected})
                </span>
              )}
            </div>
          ) : (
            <p className="text-xs text-amber-600 mt-0.5 font-medium">⚠ Not yet selected</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {existing && !expanded && (
            <span className="text-xs text-blue-600 hover:underline">Change</span>
          )}
          <ChevronRight size={15} className={`text-gray-400 transition-transform ${expanded?'rotate-90':''}`}/>
        </div>
      </div>

      {/* Options */}
      {expanded && (
        <div className="border-t border-gray-100 p-4">
          {material.is_structural && (
            <div className="mb-3 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <Lock size={12} className="text-blue-600 mt-0.5 flex-shrink-0"/>
              <p className="text-xs text-blue-700">
                RBD minimum quality standard applies. All options below meet our structural requirements.
                Customer may choose preferred brand.
              </p>
            </div>
          )}
          {!material.has_options && (
            <p className="text-sm text-gray-400 italic text-center py-3">
              No brand options in rate master for this category yet.
            </p>
          )}
          {tiers.map(tier => {
            const opts = material.options.filter(o=>o.quality_tier===tier);
            if (!opts.length) return null;
            return (
              <div key={tier} className="mb-3">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <span className={`px-1.5 py-0.5 rounded ${TIER_BADGE[tier]}`}>{tier}</span>
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {opts.map(opt => {
                    const isSelected = existing?.variant_id === opt.variant_id;
                    return (
                      <button key={opt.variant_id} type="button"
                        onClick={()=>{
                          onSelect({
                            variant_id: opt.variant_id, mat_id: opt.mat_id,
                            brand_name: opt.brand_name, mat_name: opt.mat_name,
                            quality_tier: opt.quality_tier, rate_per_unit: opt.rate_per_unit,
                            unit: opt.unit, stage_first_selected: '', stage_id_first: 0,
                          }, 0, '');
                          setExpanded(false);
                        }}
                        className={`flex items-center justify-between p-3 rounded-lg border-2 text-left transition-all
                          ${isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : `border-gray-200 hover:border-blue-300 bg-white`}`}>
                        <div>
                          <p className={`text-sm font-semibold ${isSelected?'text-blue-700':'text-gray-800'}`}>
                            {opt.brand_name}</p>
                          <p className="text-xs text-gray-400">{opt.mat_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-700">
                            ₹{Number(opt.rate_per_unit).toLocaleString()}/{opt.unit}</p>
                          {isSelected && <CheckCircle size={14} className="text-blue-600 ml-auto mt-0.5"/>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Stage Step ────────────────────────────────────────────────
const StageStep = ({
  stage, materials, selections, totalSft,
  onSelect, onNext, onPrev, isFirst, isLast, stageIndex, totalStages
}: {
  stage: Stage;
  materials: MaterialCategory[];
  selections: Record<string, Selection>;
  totalSft: number;
  onSelect: (category: string, sel: Selection, stageId: number, stageName: string) => void;
  onNext: () => void;
  onPrev: () => void;
  isFirst: boolean;
  isLast: boolean;
  stageIndex: number;
  totalStages: number;
}) => {
  const newCategories = materials.filter(m => !selections[m.category]);
  const alreadySelected = materials.filter(m => !!selections[m.category]);
  const allSelected = materials.every(m => !!selections[m.category]);

  return (
    <div>
      {/* Stage header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-gray-400">{stage.stage_code}</span>
            <h2 className="text-lg font-bold text-gray-900">{stage.stage_name}</h2>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            Stage {stageIndex+1} of {totalStages} ·
            {materials.length} material categories ·
            {newCategories.length > 0
              ? <span className="text-amber-600"> {newCategories.length} new selection(s) needed</span>
              : <span className="text-green-600"> all already selected ✓</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">
            {Object.keys(selections).length} total materials selected
          </span>
        </div>
      </div>

      {/* Already selected — compact */}
      {alreadySelected.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-bold text-green-700 mb-2 flex items-center gap-1">
            <CheckCircle size={12}/> Already Selected ({alreadySelected.length})</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {alreadySelected.map(mat => {
              const sel = selections[mat.category];
              return (
                <div key={mat.category}
                  className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <div>
                    <p className="text-xs font-semibold text-gray-800">{mat.display_name}</p>
                    <p className="text-xs text-gray-500">
                      {sel.brand_name} · ₹{Number(sel.rate_per_unit).toLocaleString()}/{sel.unit}
                    </p>
                    <p className="text-xs text-gray-400">Set in: {sel.stage_first_selected}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${TIER_BADGE[sel.quality_tier]}`}>
                      {sel.quality_tier}</span>
                    <CheckCircle size={14} className="text-green-500"/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* New materials to select */}
      {newCategories.length > 0 && (
        <div className="space-y-3 mb-4">
          <p className="text-xs font-bold text-amber-700 flex items-center gap-1">
            <Star size={12}/> Select Brand for New Materials ({newCategories.length})</p>
          {newCategories.map(mat => (
            <BrandPicker key={mat.category} material={mat}
              existing={selections[mat.category]||null}
              onSelect={(sel, sId, sName) =>
                onSelect(mat.category, {
                  ...sel,
                  stage_first_selected: stage.stage_name,
                  stage_id_first: stage.stage_id,
                }, stage.stage_id, stage.stage_name)
              }/>
          ))}
        </div>
      )}

      {newCategories.length === 0 && (
        <div className="text-center py-8 bg-green-50 rounded-xl border border-green-200 mb-4">
          <CheckCircle size={32} className="text-green-500 mx-auto mb-2"/>
          <p className="text-sm font-semibold text-green-700">All materials for this stage already selected!</p>
          <p className="text-xs text-green-600 mt-1">You can proceed to the next stage.</p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button onClick={onPrev} disabled={isFirst}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-40">
          <ChevronLeft size={16}/> Previous Stage</button>
        <button onClick={onNext}
          className={`flex items-center gap-2 px-6 py-2 text-sm rounded-lg font-medium transition-colors
            ${allSelected || newCategories.length===0
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-amber-500 text-white hover:bg-amber-600'}`}>
          {isLast ? 'Review & Freeze' : 'Next Stage'}
          <ChevronRight size={16}/>
        </button>
      </div>
    </div>
  );
};

// ── Review & Freeze Step ──────────────────────────────────────
const ReviewStep = ({
  selections, stages, estimateId, estimateCode, projectId,
  totalSft, buildingType, floors,
  onPrev, onFrozen
}: {
  selections: Record<string,Selection>;
  stages: Stage[];
  estimateId: number;
  estimateCode: string;
  projectId: number|null;
  totalSft: number;
  buildingType: string;
  floors: number;
  onPrev: ()=>void;
  onFrozen: (specId: number) => void;
}) => {
  const [freezing, setFreezing] = useState(false);
  const [error, setError]       = useState('');

  const categories = Object.entries(selections);
  const structural = categories.filter(([,v])=>v.quality_tier && ['Cement','Steel','Aggregate','Sand','Masonry'].includes(v.mat_name?.split(' ')[0]||''));
  const totalMaterialCost = categories.reduce((sum,[,v])=>sum+(totalSft*(0.44*Number(v.rate_per_unit))),0);

  const freeze = async () => {
    setFreezing(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      const r = await api.post('/estimation/material-spec', {
        estimate_id: estimateId,
        project_id:  projectId,
        selections,
      });
      onFrozen(r.data.data.spec_id);
    } catch(e:any) { setError(e.response?.data?.message||'Failed to freeze'); }
    finally { setFreezing(false); }
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-bold text-gray-900">Review & Freeze Material Spec</h2>
        <p className="text-xs text-gray-400 mt-0.5">
          {estimateCode} · {buildingType} · {floors}F ·
          {totalSft.toLocaleString()} SFT ·
          {categories.length} materials selected
        </p>
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>}

      {/* Warning */}
      <div className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
        <Lock size={18} className="text-amber-600 mt-0.5 flex-shrink-0"/>
        <div>
          <p className="text-sm font-bold text-amber-800">Freezing this specification</p>
          <p className="text-xs text-amber-700 mt-1">
            Once frozen, this material spec will be linked to estimate {estimateCode} and becomes
            the reference document for the construction agreement. Rates are locked as of today.
          </p>
        </div>
      </div>

      {/* Consolidated table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-5">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <p className="text-sm font-bold text-gray-700">Consolidated Material List ({categories.length})</p>
          <div className="flex gap-2">
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
              {categories.filter(([,v])=>v.quality_tier==='PREMIUM'||v.quality_tier==='LUXURY').length} Premium/Luxury
            </span>
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
              {categories.filter(([,v])=>v.quality_tier==='ECONOMY'||v.quality_tier==='STANDARD').length} Economy/Standard
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Material Category</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Selected Brand</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Tier</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Rate</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">First Used In</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {/* Structural first */}
              <tr className="bg-blue-50">
                <td colSpan={5} className="px-4 py-1.5">
                  <p className="text-xs font-bold text-blue-700 flex items-center gap-1">
                    <Shield size={11}/> Structural Materials</p>
                </td>
              </tr>
              {categories.filter(([cat]) => {
                const structural_cats = ['Cement','Steel','Aggregate','Sand','Masonry'];
                return structural_cats.includes(cat);
              }).map(([cat, sel]) => (
                <tr key={cat} className="hover:bg-blue-50/50">
                  <td className="px-4 py-2.5 font-medium text-gray-800 text-xs">{cat}</td>
                  <td className="px-4 py-2.5">
                    <p className="text-xs font-semibold text-gray-900">{sel.brand_name}</p>
                    <p className="text-xs text-gray-400">{sel.mat_name}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TIER_BADGE[sel.quality_tier]||''}`}>
                      {sel.quality_tier}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs font-bold text-gray-700">
                    ₹{Number(sel.rate_per_unit).toLocaleString()}/{sel.unit}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-400">{sel.stage_first_selected}</td>
                </tr>
              ))}
              {/* Interior */}
              <tr className="bg-amber-50">
                <td colSpan={5} className="px-4 py-1.5">
                  <p className="text-xs font-bold text-amber-700 flex items-center gap-1">
                    <Star size={11}/> Interior & Finishing Materials</p>
                </td>
              </tr>
              {categories.filter(([cat]) => {
                const structural_cats = ['Cement','Steel','Aggregate','Sand','Masonry'];
                return !structural_cats.includes(cat);
              }).map(([cat, sel]) => (
                <tr key={cat} className="hover:bg-amber-50/50">
                  <td className="px-4 py-2.5 font-medium text-gray-800 text-xs">{cat}</td>
                  <td className="px-4 py-2.5">
                    <p className="text-xs font-semibold text-gray-900">{sel.brand_name}</p>
                    <p className="text-xs text-gray-400">{sel.mat_name}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TIER_BADGE[sel.quality_tier]||''}`}>
                      {sel.quality_tier}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs font-bold text-gray-700">
                    ₹{Number(sel.rate_per_unit).toLocaleString()}/{sel.unit}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-400">{sel.stage_first_selected}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button onClick={onPrev}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">
          <ChevronLeft size={16}/> Back</button>
        <button onClick={freeze} disabled={freezing || categories.length===0}
          className="flex items-center gap-2 px-8 py-2.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50">
          <Lock size={15}/>
          {freezing ? 'Freezing...' : 'Freeze & Lock Material Spec'}
        </button>
      </div>
    </div>
  );
};

// ── Spec Frozen Success ───────────────────────────────────────
const SpecFrozen = ({ specId, onViewAll, onNewSpec }: {
  specId: number; onViewAll: ()=>void; onNewSpec: ()=>void;
}) => (
  <div className="text-center py-16">
    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <Lock size={36} className="text-green-600"/>
    </div>
    <h2 className="text-xl font-bold text-gray-900 mb-2">Material Spec Frozen!</h2>
    <p className="text-sm text-gray-500 mb-1">Spec #{specId} locked and saved.</p>
    <p className="text-xs text-gray-400 mb-6">
      This specification is now linked to the estimate and will be used for the construction agreement.
    </p>
    <div className="flex gap-3 justify-center">
      <button onClick={onViewAll}
        className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50">
        <List size={14}/> View All Specs</button>
      <button onClick={onNewSpec}
        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
        <Plus size={14}/> New Material Spec</button>
    </div>
  </div>
);

// ── Wizard ────────────────────────────────────────────────────
const MaterialSpecWizard = ({ onViewAll }: { onViewAll: ()=>void }) => {
  // Step 0: select estimate
  const [wizardStep, setWizardStep]   = useState<'setup'|'stages'|'review'|'done'>('setup');
  const [estimates, setEstimates]     = useState<any[]>([]);
  const [selectedEstId, setSelectedEstId] = useState('');
  const [selectedEst, setSelectedEst] = useState<any>(null);
  const [projectId, setProjectId]     = useState<number|null>(null);

  // Stages wizard
  const [stages, setStages]           = useState<Stage[]>([]);
  const [stageIndex, setStageIndex]   = useState(0);
  const [stageMaterials, setStageMaterials] = useState<MaterialCategory[]>([]);
  const [loadingMats, setLoadingMats] = useState(false);
  const [selections, setSelections]   = useState<Record<string,Selection>>({});
  const [frozenSpecId, setFrozenSpecId] = useState<number|null>(null);

  useEffect(() => {
    const load = async () => {
      const { default: api } = await import('../../utils/api');
      const r = await api.get('/estimation', { params:{ status:'DRAFT' } });
      setEstimates(r.data.data||[]);
    };
    load();
  }, []);

  const startWizard = async () => {
    if (!selectedEstId) return;
    const est = estimates.find(e=>e.estimate_id===Number(selectedEstId));
    setSelectedEst(est);
    // Load stages with materials
    const { default: api } = await import('../../utils/api');
    const r = await api.get('/estimation/stages-with-materials');
    setStages(r.data.data);
    setStageIndex(0);
    setWizardStep('stages');
    // Load first stage materials
    await loadStageMaterials(r.data.data[0].stage_id);
  };

  const loadStageMaterials = async (stageId: number) => {
    setLoadingMats(true);
    try {
      const { default: api } = await import('../../utils/api');
      const r = await api.get(`/estimation/stage-materials/${stageId}`);
      setStageMaterials(r.data.data);
    } finally { setLoadingMats(false); }
  };

  const handleSelect = (category: string, sel: Selection, stageId: number, stageName: string) => {
    setSelections(prev => ({
      ...prev,
      [category]: {
        ...sel,
        stage_first_selected: prev[category]?.stage_first_selected || stageName,
        stage_id_first: prev[category]?.stage_id_first || stageId,
      }
    }));
  };

  const goNext = async () => {
    if (stageIndex < stages.length - 1) {
      const nextIdx = stageIndex + 1;
      setStageIndex(nextIdx);
      await loadStageMaterials(stages[nextIdx].stage_id);
    } else {
      setWizardStep('review');
    }
  };

  const goPrev = async () => {
    if (stageIndex > 0) {
      const prevIdx = stageIndex - 1;
      setStageIndex(prevIdx);
      await loadStageMaterials(stages[prevIdx].stage_id);
    }
  };

  const totalSft = selectedEst ? selectedEst.floors * selectedEst.area_per_floor_sft : 0;

  // Progress bar
  const progress = stages.length > 0
    ? Math.round((stageIndex / stages.length) * 100) : 0;

  if (wizardStep==='done' && frozenSpecId) {
    return <SpecFrozen specId={frozenSpecId} onViewAll={onViewAll} onNewSpec={()=>{ setWizardStep('setup'); setSelections({}); setSelectedEstId(''); setSelectedEst(null); }}/>;
  }

  return (
    <div className="max-w-4xl">
      {/* Step 0: Setup */}
      {wizardStep==='setup' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-lg">
          <h2 className="text-base font-bold text-gray-900 mb-1">Start Material Specification</h2>
          <p className="text-xs text-gray-400 mb-4">
            Select an estimate to create a stage-wise material specification for the contract.
          </p>
          <div className="mb-4">
            <label className="text-xs font-medium text-gray-600 block mb-1">Select Estimate *</label>
            <select value={selectedEstId} onChange={e=>setSelectedEstId(e.target.value)} className={inp}>
              <option value="">Choose estimate...</option>
              {estimates.map(e=>(
                <option key={e.estimate_id} value={e.estimate_id}>
                  {e.estimate_code} — {e.bldg_type} {e.floors}F {Number(e.area_per_floor_sft).toLocaleString()}SFT — {e.quality_tier}
                </option>
              ))}
            </select>
            {estimates.length===0 && (
              <p className="text-xs text-amber-600 mt-1">No DRAFT estimates found. Create an estimate first.</p>
            )}
          </div>
          {selectedEstId && (() => {
            const est = estimates.find(e=>e.estimate_id===Number(selectedEstId));
            return est ? (
              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-500">Building</span>
                  <span className="font-medium">{est.bldg_type} · {est.floors} floors</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total SFT</span>
                  <span className="font-medium">{(est.floors*est.area_per_floor_sft).toLocaleString()} SFT</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Cost</span>
                  <span className="font-bold text-blue-600">
                    {Number(est.total_cost)>=10000000 ? `₹${(Number(est.total_cost)/10000000).toFixed(2)} Cr` : `₹${(Number(est.total_cost)/100000).toFixed(2)} L`}
                  </span>
                </div>
              </div>
            ) : null;
          })()}
          <button onClick={startWizard} disabled={!selectedEstId}
            className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
            Start Stage-wise Selection <ChevronRight size={15}/>
          </button>
        </div>
      )}

      {/* Stages wizard */}
      {wizardStep==='stages' && stages.length>0 && (
        <div>
          {/* Progress */}
          <div className="mb-5">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
              <span>Stage {stageIndex+1} of {stages.length}</span>
              <span>{Object.keys(selections).length} materials selected so far</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{width:`${progress}%`}}/>
            </div>
            {/* Stage pills */}
            <div className="flex flex-wrap gap-1 mt-3">
              {stages.map((s,i) => (
                <button key={s.stage_id} onClick={async ()=>{ setStageIndex(i); await loadStageMaterials(s.stage_id); }}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors
                    ${i===stageIndex ? 'bg-blue-600 text-white' :
                      i<stageIndex ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {i<stageIndex ? '✓ ' : ''}{s.stage_code}
                </button>
              ))}
            </div>
          </div>

          {loadingMats ? (
            <div className="text-center py-16">
              <RefreshCw size={24} className="animate-spin text-blue-600 mx-auto mb-2"/>
              <p className="text-sm text-gray-400">Loading stage materials...</p>
            </div>
          ) : (
            <StageStep
              stage={stages[stageIndex]}
              materials={stageMaterials}
              selections={selections}
              totalSft={totalSft}
              onSelect={handleSelect}
              onNext={goNext}
              onPrev={goPrev}
              isFirst={stageIndex===0}
              isLast={stageIndex===stages.length-1}
              stageIndex={stageIndex}
              totalStages={stages.length}
            />
          )}
        </div>
      )}

      {/* Review */}
      {wizardStep==='review' && (
        <ReviewStep
          selections={selections}
          stages={stages}
          estimateId={Number(selectedEstId)}
          estimateCode={selectedEst?.estimate_code||''}
          projectId={projectId}
          totalSft={totalSft}
          buildingType={selectedEst?.bldg_type||''}
          floors={selectedEst?.floors||0}
          onPrev={()=>{setWizardStep('stages'); loadStageMaterials(stages[stageIndex].stage_id);}}
          onFrozen={id=>{ setFrozenSpecId(id); setWizardStep('done'); }}
        />
      )}
    </div>
  );
};

// ── Saved Specs List ──────────────────────────────────────────
const SavedSpecsList = () => {
  const [specs, setSpecs]     = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail]   = useState<any>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { default: api } = await import('../../utils/api');
        const r = await api.get('/estimation/material-specs');
        setSpecs(r.data.data);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  const openDetail = async (id: number) => {
    const { default: api } = await import('../../utils/api');
    const r = await api.get(`/estimation/material-specs/${id}`);
    setDetail(r.data.data);
  };

  const printSpec = (spec: any) => {
    const items = spec.spec_items_json || {};
    const rows = Object.entries(items).map(([cat, sel]: [string, any]) =>
      `<tr><td>${cat}</td><td>${sel.brand_name}</td><td>${sel.quality_tier}</td><td>₹${Number(sel.rate_per_unit).toLocaleString()}/${sel.unit}</td><td>${sel.stage_first_selected||'—'}</td></tr>`
    ).join('');

    const html = `
      <html><head><title>Material Spec — ${spec.estimate_code}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:40px;color:#111}
        h1{font-size:18px;margin-bottom:4px}
        .sub{color:#666;font-size:12px;margin-bottom:24px}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th{background:#f3f4f6;text-align:left;padding:8px 12px;border:1px solid #e5e7eb}
        td{padding:8px 12px;border:1px solid #e5e7eb}
        .section{font-weight:bold;font-size:11px;text-transform:uppercase;color:#4B5563;
          background:#F9FAFB;padding:6px 12px;border:1px solid #e5e7eb}
        .freeze{color:#059669;font-size:12px;margin-top:20px}
        .footer{margin-top:40px;font-size:11px;color:#9CA3AF}
      </style></head><body>
      <h1>Material Specification Sheet</h1>
      <div class="sub">
        ${spec.estimate_code} · ${spec.bldg_type} · ${spec.floors} Floors ·
        ${Number(spec.area_per_floor_sft).toLocaleString()} SFT/floor ·
        Frozen: ${new Date(spec.frozen_at).toLocaleDateString('en-IN')} by ${spec.frozen_by_name}
      </div>
      <table>
        <tr>
          <th>Material Category</th><th>Selected Brand</th>
          <th>Quality Tier</th><th>Rate</th><th>First Used In Stage</th>
        </tr>
        ${rows}
      </table>
      <p class="freeze">✓ This specification is FROZEN and forms part of the construction agreement.</p>
      <div class="footer">
        Rathna Bhoomi Developer, Bengaluru · Generated on ${new Date().toLocaleDateString('en-IN')}
      </div>
      </body></html>`;

    const w = window.open('','_blank');
    if (w) { w.document.write(html); w.document.close(); w.print(); }
  };

  return (
    <>
      {detail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="font-bold text-gray-900">{detail.estimate_code} — Material Spec</h3>
                <p className="text-xs text-gray-400">
                  Frozen {fmtDate(detail.frozen_at)} by {detail.frozen_by_name}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={()=>printSpec(detail)}
                  className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700">
                  <Download size={12}/> Print / PDF</button>
                <button onClick={()=>setDetail(null)}><X size={18} className="text-gray-400"/></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {/* Shorthand specs */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {[
                  {label:'Cement',       val:detail.cement_spec},
                  {label:'Steel',        val:detail.steel_spec},
                  {label:'Tiles',        val:detail.tile_spec},
                  {label:'Paint',        val:detail.paint_spec},
                  {label:'Plumbing',     val:detail.plumbing_spec},
                  {label:'Electrical',   val:detail.electrical_spec},
                  {label:'Waterproofing',val:detail.waterproofing_spec},
                ].filter(s=>s.val).map(s=>(
                  <div key={s.label} className="bg-gray-50 rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-500">{s.label}</p>
                    <p className="text-xs font-semibold text-gray-800">{s.val}</p>
                  </div>
                ))}
              </div>
              {/* Full table */}
              {detail.spec_items_json && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-gray-500">Category</th>
                        <th className="text-left px-3 py-2 font-medium text-gray-500">Brand</th>
                        <th className="text-left px-3 py-2 font-medium text-gray-500">Tier</th>
                        <th className="text-right px-3 py-2 font-medium text-gray-500">Rate</th>
                        <th className="text-left px-3 py-2 font-medium text-gray-500">Stage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {Object.entries(detail.spec_items_json).map(([cat, sel]:any)=>(
                        <tr key={cat} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium text-gray-800">{cat}</td>
                          <td className="px-3 py-2">
                            <p className="font-semibold text-gray-900">{sel.brand_name}</p>
                            <p className="text-gray-400">{sel.mat_name}</p>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full font-medium ${TIER_BADGE[sel.quality_tier]||''}`}>
                              {sel.quality_tier}</span>
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-700">
                            ₹{Number(sel.rate_per_unit).toLocaleString()}/{sel.unit}
                          </td>
                          <td className="px-3 py-2 text-gray-400">{sel.stage_first_selected||'—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Estimate</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Building</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Project</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Key Materials</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Frozen By</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Date</th>
              <th className="px-4 py-3"/>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? [...Array(3)].map((_,i)=>(
              <tr key={i}>{[...Array(7)].map((_,j)=>(
                <td key={j} className="px-4 py-3"><div className="bg-gray-100 rounded h-4 animate-pulse"/></td>
              ))}</tr>
            )) : specs.length===0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                No frozen specs yet. Create one using the wizard.
              </td></tr>
            ) : specs.map(s=>(
              <tr key={s.spec_id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="text-xs font-medium text-gray-900">{s.estimate_code}</p>
                  <p className="text-xs text-gray-400">{s.bldg_type} · {s.floors}F</p>
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {Number(s.area_per_floor_sft).toLocaleString()} SFT/floor
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{s.project_name||'—'}</td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {s.cement_spec && <div>Cement: <span className="font-medium">{s.cement_spec.split('(')[0]}</span></div>}
                  {s.steel_spec  && <div>Steel: <span className="font-medium">{s.steel_spec.split('(')[0]}</span></div>}
                  {s.tile_spec   && <div>Tiles: <span className="font-medium">{s.tile_spec.split('(')[0]}</span></div>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">{s.frozen_by_name}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{fmtDate(s.frozen_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={()=>openDetail(s.spec_id)}
                      className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-lg hover:bg-blue-100">
                      <Eye size={11}/> View</button>
                    <button onClick={()=>{ openDetail(s.spec_id).then(()=>printSpec(s)); }}
                      className="flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-lg hover:bg-gray-200">
                      <Download size={11}/> PDF</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ── Main MaterialSpecPage ─────────────────────────────────────
const MaterialSpecPage = () => {
  const [tab, setTab] = useState<'wizard'|'saved'>('wizard');
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Layers size={22} className="text-blue-600"/>
          Material Specification Wizard
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Stage-wise material selection · Auto-skips already selected · Freeze for contract
        </p>
      </div>
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
        {([
          {key:'wizard',label:'New Specification',icon:<Plus size={14}/>},
          {key:'saved', label:'Saved Specs',       icon:<List size={14}/>},
        ] as const).map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors
              ${tab===t.key?'bg-white text-gray-900 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
            {t.icon}{t.label}</button>
        ))}
      </div>
      {tab==='wizard' && <MaterialSpecWizard onViewAll={()=>setTab('saved')}/>}
      {tab==='saved'  && <SavedSpecsList/>}
    </div>
  );
};

export default MaterialSpecPage;
